'use client';

import { useEffect, useCallback, useRef, useReducer } from "react";
import { ymmApiClient } from "../api/client";
import { parseDefaultApiResponse, parseOnChangeApiResponse } from "../api/parser";
import { ymmFormReducer } from "../store/reducer";
import { initialState } from "../store/state";
import { ymmFormActions } from "../store/actions";
import { YMM_FIELDS } from "../store/constants";
import { getYmmFromStorage, setYmmToStorage, clearYmmStorage } from "../utils/ymm-storage";

export function useYmmForm() {
  const [state, dispatch] = useReducer(ymmFormReducer, initialState);
  const ymmClient = useRef(ymmApiClient()).current;

  useEffect(() => {
    const saved = getYmmFromStorage();

    if (saved) {
      dispatch(ymmFormActions.setLoading('year', true));
      dispatch(ymmFormActions.setLoading('make', true));
      dispatch(ymmFormActions.setLoading('model', true));

      // One request: get_dropdowns with saved tags; skip getDefaultData
      const tags = [saved.year, saved.make, saved.model].join(',');

      ymmClient.getDropdownsData(tags)
        .then((dropdownData: Record<string, unknown>) => {
          if (dropdownData.show_app === 'disabled' || dropdownData.checkhtml === 'not-set-yet') {
            dispatch(ymmFormActions.setAppDisabled(true, String(dropdownData.disabled_message ?? '')));
            return;
          }

          const html = (dropdownData.html ?? dropdownData.ymmlist ?? '');

          if (!html) throw new Error('get_dropdowns response missing html/ymmlist');

          const parsed = parseOnChangeApiResponse({ ...dropdownData, html } as Parameters<typeof parseOnChangeApiResponse>[0]);
          dispatch(ymmFormActions.setFields(parsed.fields));
          dispatch(ymmFormActions.setYear(saved.year));
          dispatch(ymmFormActions.setMake(saved.make));
          dispatch(ymmFormActions.setModel(saved.model));
          dispatch(ymmFormActions.setPreserveFromRestore(true));
        })
        .catch((error) => {
          console.error(error);
          dispatch(ymmFormActions.setError(error.message));
        })
        .finally(() => {
          dispatch(ymmFormActions.setLoading('year', false));
          dispatch(ymmFormActions.setLoading('make', false));
          dispatch(ymmFormActions.setLoading('model', false));
        });
      return;
    }

    dispatch(ymmFormActions.setLoading('year', true));
    ymmClient.getDefaultData()
      .then((data) => {
        if (data?.show_app === 'disabled' || data?.checkhtml === 'not-set-yet') {
          dispatch(ymmFormActions.setAppDisabled(true, String(data.disabled_message ?? '')));
          return;
        }

        const parsedData = parseDefaultApiResponse(data);
        dispatch(ymmFormActions.setFields(parsedData.fields));
      })
      .catch((error) => {
        console.error(error);
        dispatch(ymmFormActions.setError(error.message));
      })
      .finally(() => {
        dispatch(ymmFormActions.setLoading('year', false));
      });
  }, [ymmClient]);

  // Set year handler
  const setYear = useCallback((value: string) => {
    if (state.isLoading.make || state.fieldState.year.current_val === value) {
      return;
    }

    dispatch(ymmFormActions.setYear(value));
    dispatch(ymmFormActions.resetDependentFields('year'));

    // Clear preserve flag on first real user change (value !== current_val means user changed it)
    if (state.preserveFromRestore) {
      dispatch(ymmFormActions.setPreserveFromRestore(false));
    }

    dispatch(ymmFormActions.setLoading('make', true));
    const previousValue = value;

    ymmClient.getOptionHtml({
      current_field: YMM_FIELDS.YEAR,
      prev_val: previousValue,
      current_val: value,
    })
      .then((data) => {
        const parsedData = parseOnChangeApiResponse(data);
        dispatch(ymmFormActions.setFields(parsedData.fields));
      })
      .catch((error) => {
        dispatch(ymmFormActions.setError(error.message));
      })
      .finally(() => {
        dispatch(ymmFormActions.setLoading('make', false));
      });
  }, [state.isLoading.make, state.fieldState.year.current_val, state.preserveFromRestore, ymmClient]);

  // Set make handler
  const setMake = useCallback((value: string) => {
    if (state.isLoading.year || state.fieldState.make.current_val === value) {
      return;
    }

    dispatch(ymmFormActions.setMake(value));
    dispatch(ymmFormActions.resetDependentFields('make'));

    // Clear preserve flag on first real user change (value !== current_val means user changed it)
    if (state.preserveFromRestore) {
      dispatch(ymmFormActions.setPreserveFromRestore(false));
    }

    dispatch(ymmFormActions.setLoading('model', true));

    const previousValue = [state.fieldState.year.current_val, value].join(',');

    ymmClient.getOptionHtml({
      current_field: YMM_FIELDS.MAKE,
      prev_val: previousValue,
      current_val: value,
    })
      .then((data) => {
        const parsedData = parseOnChangeApiResponse(data);
        dispatch(ymmFormActions.setFields(parsedData.fields));
      })
      .catch((error) => {
        dispatch(ymmFormActions.setError(error.message));
      })
      .finally(() => {
        dispatch(ymmFormActions.setLoading('model', false));
      });
  }, [
    state.fieldState.year.current_val,
    state.isLoading.model,
    state.fieldState.make.current_val,
    state.preserveFromRestore,
    ymmClient,
  ]);

  // Set model handler
  const setModel = useCallback((value: string) => {
    if (state.isLoading.year || state.isLoading.make || state.fieldState.model.current_val === value) {
      return;
    }

    dispatch(ymmFormActions.setModel(value));

    // Clear preserve flag on first real user change (value !== current_val means user changed it)
    if (state.preserveFromRestore) {
      dispatch(ymmFormActions.setPreserveFromRestore(false));
    }
  }, [
    state.isLoading.model,
    state.fieldState.model.current_val,
    state.preserveFromRestore,
    ymmClient,
  ]);

  // Reset selections handler
  const resetSelections = useCallback(() => {
    clearYmmStorage();
    dispatch(ymmFormActions.setPreserveFromRestore(false));
    dispatch(ymmFormActions.resetFields());
    dispatch(ymmFormActions.resetDependentFields('year'));
  }, [dispatch]);

  // Persist YMM to localStorage only when user has searched (all three selected). Call from onSearch.
  const persistYmmSelection = useCallback(() => {
    const { year, make, model } = state.fieldState;

    if (year.current_val && make.current_val && model.current_val) {
      setYmmToStorage({
        year: year.current_val,
        make: make.current_val,
        model: model.current_val,
      });
    }
  }, [
    state.fieldState.year.current_val,
    state.fieldState.make.current_val,
    state.fieldState.model.current_val,
  ]);

  /** Call when a form submits (Search). Clears preserveFromRestore so subsequent field changes trigger getOptionHtml again. */
  const clearPreserveFromRestore = useCallback(() => {
    dispatch(ymmFormActions.setPreserveFromRestore(false));
  }, [dispatch]);

  return {
    ...state,
    setYear,
    setMake,
    setModel,
    resetSelections,
    persistYmmSelection,
    clearPreserveFromRestore,
  };
}